// components/filmManagement.jsx
// =====================================================================
// FILM MANAGEMENT — Production House Admin Panel
// Dark teal theme, matches the redesigned Production House Dashboard.
// Section navigation now lives in the main dashboard sidebar (hover flyout on
// "Film Management"); this component is controlled via `section` / `onSectionChange`
// and falls back to its own state when used standalone.
//
// ⚠️  BACKEND NOTES (ask to add if missing):
//   1. GET  /getUserByUsername/{username}           → search exact username
//   2. GET  /production/admin/leaves/pending        → list pending leaves
//   3. PUT  /production/admin/leaves/{id}/review    → approve / reject leave
//   4. GET  /production/admin/feedback              → list all feedback
//   5. GET  /production/admin/complaints            → list all complaints
//   6. PUT  /production/admin/complaints/{id}/update→ update complaint status
//   7. POST /production/admin/invitations/create    → accept optional `expiry_minutes` field
// =====================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { baseUrl } from '../config';
import headerBg from '../assets/img/filmmanagement.jpg';
import pendingEmptyImg from '../assets/img/mockupfm.jpg';
import leaveEmptyImg from '../assets/img/leaverqfm.jpg';
import complaintsEmptyImg from '../assets/img/complaintsfm.jpg';
import projectsEmptyImg from '../assets/img/pafm.jpg';

// ─────────────────────────────────────────────
// Icons (inline SVG — no dependency)
// ─────────────────────────────────────────────

const PATHS = {
  clapper: (<><path d="M4 11v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8H4z" /><path d="M4 11l-.88-3.12a1 1 0 0 1 .7-1.23l13.5-3.8a1 1 0 0 1 1.23.7L19.4 7" /><line x1="7.5" y1="5.9" x2="9.6" y2="9.7" /><line x1="12.3" y1="4.6" x2="14.4" y2="8.3" /></>),
  grid: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  userPlus: (<><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>),
  users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  key: (<><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.7 12.3L21 2" /><path d="M17 6l3 3" /><path d="M15 8l3 3" /></>),
  hourglass: (<path d="M6 2h12M6 22h12M6 2c0 6 6 6 6 10s-6 4-6 10M18 2c0 6-6 6-6 10s6 4 6 10" />),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  message: (<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />),
  alert: (<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>),
  film: (<><rect x="2" y="2" width="20" height="20" rx="2.18" /><line x1="7" y1="2" x2="7" y2="22" /><line x1="17" y1="2" x2="17" y2="22" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="2" y1="7" x2="7" y2="7" /><line x1="2" y1="17" x2="7" y2="17" /><line x1="17" y1="17" x2="22" y2="17" /><line x1="17" y1="7" x2="22" y2="7" /></>),
  mail: (<><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 6 12 13 2 6" /></>),
  fileText: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>),
  search: (<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  info: (<><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>),
  pencil: (<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></>),
  timer: (<><circle cx="12" cy="13" r="8" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="13" x2="14.5" y2="15.5" /><line x1="10" y1="2" x2="14" y2="2" /></>),
  check: (<polyline points="20 6 9 17 4 12" />),
  x: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  refresh: (<><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>),
  copy: (<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>),
  send: (<><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>),
  shield: (<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />),
  chevronRight: (<polyline points="9 18 15 12 9 6" />),
  tool: (<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />),
};

const Ico = ({ name, size = 18, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {PATHS[name]}
  </svg>
);

// Section registry (labels + icons used by banners and the overview grid)
export const FILM_SECTIONS = [
  { id: 'overview',        label: 'Overview',            icon: 'grid' },
  { id: 'invite-username', label: 'Invite by Username',  icon: 'userPlus' },
  { id: 'invite-code',     label: 'Invite via Code',     icon: 'key' },
  { id: 'pending',         label: 'Pending Connections', icon: 'hourglass' },
  { id: 'leaves',          label: 'Leave Requests',      icon: 'calendar' },
  { id: 'feedback',        label: 'Feedback',            icon: 'message' },
  { id: 'complaints',      label: 'Complaints',          icon: 'alert' },
  { id: 'projects',        label: 'Project Allocation',  icon: 'film' },
];

// ─────────────────────────────────────────────
// Small reusable atoms
// ─────────────────────────────────────────────

const Spinner = () => (
  <div className="fm-spinner-wrap">
    <div className="fm-spinner" />
  </div>
);

const Badge = ({ label, color }) => (
  <span className={`fm-badge fm-badge--${color || 'gray'}`}>{label}</span>
);

const StatCard = ({ icon, tone, label, value, desc }) => (
  <div className="fm-stat">
    <span className={`fm-tile fm-tone-${tone}`}><Ico name={icon} size={22} /></span>
    <div className="fm-stat-text">
      <div className="fm-stat-value">{value}</div>
      <div className="fm-stat-label">{label}</div>
      {desc && <div className="fm-stat-desc">{desc}</div>}
    </div>
  </div>
);

const CardHead = ({ icon, tone = 'teal', title, subtitle, right }) => (
  <div className="fm-card-head">
    {icon && <span className={`fm-tile fm-tile--sm fm-tone-${tone}`}><Ico name={icon} size={20} /></span>}
    <div className="fm-card-head-text">
      <h3 className="fm-card-title">{title}</h3>
      {subtitle && <p className="fm-card-sub">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const EmptyState = ({ image, icon, title, message }) => (
  <div className="fm-empty">
    {image ? (
      <img src={image} alt="" className="fm-empty-img" />
    ) : (
      <span className="fm-empty-icon"><Ico name={icon || 'info'} size={28} /></span>
    )}
    {title && <p className="fm-empty-title">{title}</p>}
    <p className="fm-empty-msg">{message}</p>
  </div>
);

const Alert = ({ type = 'success', children }) => (
  <div className={`fm-alert fm-alert--${type}`}>{children}</div>
);

// Header banner used by every section
const Banner = ({ sectionLabel, title, subtitle, onRoot, showIcon }) => (
  <header className="fm-banner">
    <div className="fm-banner-bg" style={{ backgroundImage: `url(${headerBg})` }} />
    <div className="fm-banner-overlay" />
    <div className="fm-banner-content">
      <nav className="fm-crumbs" aria-label="Film Management">
        {sectionLabel === 'Overview' ? (
          <span>Film Management</span>
        ) : (
          <button type="button" onClick={onRoot}>Film Management</button>
        )}
        <span className="fm-crumb-sep">/</span>
        <span className="fm-crumb-current">{sectionLabel}</span>
      </nav>
      <div className="fm-banner-main">
        {showIcon && <span className="fm-banner-icon"><Ico name="clapper" size={24} /></span>}
        <div>
          <h2 className="fm-banner-title">{title}</h2>
          {subtitle && <p className="fm-banner-sub">{subtitle}</p>}
        </div>
      </div>
    </div>
  </header>
);

const ComingSoon = ({ feature }) => (
  <div className="fm-card">
    <EmptyState icon="tool" title={feature} message="This feature is under development. Backend endpoint integration pending." />
  </div>
);

// ─────────────────────────────────────────────
// Invitation Code Section
// ─────────────────────────────────────────────

const DEFAULT_MESSAGE =
  "You've been invited to join our production house on Producer Buddy. Use this security code to connect with us. We look forward to collaborating with you!";

const EXPIRY_OPTIONS = [
  { label: '10 minutes', value: 10 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
  { label: '4 hours', value: 240 },
  { label: '6 hours', value: 360 },
  { label: '12 hours', value: 720 },
];

const InviteByCodeSection = ({ token }) => {
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [isEditingMsg, setIsEditingMsg] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);

  // Restore active code from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('pb_inv_code');
    const savedExpiry = localStorage.getItem('pb_inv_expiry');
    if (saved && savedExpiry) {
      const expDate = new Date(savedExpiry);
      if (expDate > new Date()) {
        setGeneratedCode(saved);
        setExpiresAt(expDate);
      } else {
        localStorage.removeItem('pb_inv_code');
        localStorage.removeItem('pb_inv_expiry');
      }
    }
  }, []);

  // Countdown effect
  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => {
      const diff = expiresAt - new Date();
      if (diff <= 0) {
        setCountdown('Expired');
        setGeneratedCode(null);
        setExpiresAt(null);
        localStorage.removeItem('pb_inv_code');
        localStorage.removeItem('pb_inv_expiry');
        clearInterval(intervalRef.current);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(
        `${h > 0 ? h + 'h ' : ''}${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
      );
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [expiresAt]);

  const generateCode = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${baseUrl}/production/admin/invitations/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invited_user_id: null,
          invited_email: null,
          role_offered: null,
          project_id: null,
          expiry_minutes: expiryMinutes, // ⚠️ Backend needs to support this field
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const expDate = new Date(Date.now() + expiryMinutes * 60 * 1000);
        setGeneratedCode(data.invitation_code);
        setExpiresAt(expDate);
        localStorage.setItem('pb_inv_code', data.invitation_code);
        localStorage.setItem('pb_inv_expiry', expDate.toISOString());
      } else {
        setError('Failed to generate code. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = () => {
    if (!generatedCode) return;
    navigator.clipboard.writeText(generatedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearCode = () => {
    setGeneratedCode(null);
    setExpiresAt(null);
    setCountdown('');
    localStorage.removeItem('pb_inv_code');
    localStorage.removeItem('pb_inv_expiry');
  };

  // UI-only: reset the form (message + expiry) to defaults
  const resetForm = () => {
    setMessage(DEFAULT_MESSAGE);
    setIsEditingMsg(false);
    setExpiryMinutes(60);
    setError('');
  };

  return (
    <div className="fm-card">
      <CardHead
        icon="mail"
        tone="amber"
        title="Invite via Production House Code"
        subtitle="Generate a time-limited security code your team can use to join this Production House."
        right={
          <button type="button" className="fm-btn fm-btn-ghost fm-btn-sm" onClick={() => setIsEditingMsg(v => !v)}>
            <Ico name={isEditingMsg ? 'check' : 'pencil'} size={15} /> {isEditingMsg ? 'Done' : 'Edit'}
          </button>
        }
      />

      {/* Message Template */}
      <div className="fm-indent">
        {isEditingMsg ? (
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            className="fm-input fm-textarea"
            aria-label="Invitation message"
          />
        ) : (
          <p className="fm-quote">"{message}"</p>
        )}
      </div>

      {/* Expiry Selector */}
      <div className="fm-subsection">
        <CardHead
          icon="timer"
          tone="sky"
          title={<>Code expiry duration <span className="fm-muted fm-small">(max 12 hours)</span></>}
          subtitle="Set how long the invitation code will remain valid."
        />
        <div className="fm-indent fm-pills">
          {EXPIRY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setExpiryMinutes(opt.value)}
              className={`fm-pill ${expiryMinutes === opt.value ? 'is-active' : ''}`}
              aria-pressed={expiryMinutes === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && <Alert type="error">{error}</Alert>}

      {/* Generate Button */}
      {!generatedCode && (
        <div className="fm-actions">
          <button type="button" className="fm-btn fm-btn-ghost" onClick={resetForm} disabled={generating}>
            Clear
          </button>
          <button type="button" onClick={generateCode} disabled={generating} className="fm-btn fm-btn-primary">
            {generating ? (
              <><span className="fm-spinner fm-spinner--sm" /> Generating...</>
            ) : (
              <><Ico name="key" size={16} /> Generate Invitation Code</>
            )}
          </button>
        </div>
      )}

      {/* Generated Code Display */}
      {generatedCode && (
        <div className="fm-code-panel">
          <div className="fm-code-top">
            <div>
              <p className="fm-eyebrow">Your invitation code</p>
              <div className="fm-code-row">
                <span className="fm-code">{generatedCode}</span>
                <button type="button" onClick={copyCode} className={`fm-btn fm-btn-sm ${copied ? 'fm-btn-success' : 'fm-btn-ghost'}`}>
                  <Ico name={copied ? 'check' : 'copy'} size={15} /> {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="fm-countdown">
              <p className="fm-eyebrow">Expires in</p>
              <span className={countdown === 'Expired' ? 'is-expired' : ''}>{countdown}</span>
            </div>
          </div>

          {/* Security Note */}
          <div className="fm-note">
            <Ico name="shield" size={18} className="fm-note-icon" />
            <div>
              <p><strong>This is a security code.</strong> Ask your team to enter it on Producer Buddy to connect with your Production House.</p>
              <p className="fm-note-warn">This code is case-sensitive. Don't share it with anyone you don't know personally.</p>
            </div>
          </div>

          {/* Message Preview */}
          <div className="fm-preview">
            <p className="fm-eyebrow">Message preview</p>
            <p className="fm-preview-text">"{message}"</p>
          </div>

          {/* Actions Row */}
          <div className="fm-code-actions">
            <button type="button" onClick={generateCode} disabled={generating} className="fm-link">
              <Ico name="refresh" size={14} /> Regenerate new code
            </button>
            <button type="button" onClick={clearCode} className="fm-link fm-link--danger">
              <Ico name="x" size={14} /> Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Invite by Username Section
// ─────────────────────────────────────────────

const InviteByUsernameSection = ({ token }) => {
  const [username, setUsername] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [roleOffered, setRoleOffered] = useState('');
  const [inviting, setInviting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const ROLES = ['Actor', 'Director', 'Cinematographer', 'Editor', 'Sound Designer', 'Producer', 'Crew Member', 'Other'];

  const handleSearch = async () => {
    const trimmed = username.trim();
    if (!trimmed) return;
    setSearching(true);
    setSearchError('');
    setFoundUser(null);
    setSuccessMsg('');
    try {
      // ⚠️ Backend: needs GET /getUserByUsername/{username}
      const res = await fetch(`${baseUrl}/getUserByUsername/${encodeURIComponent(trimmed)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFoundUser(data);
      } else if (res.status === 404) {
        setSearchError('No user found with this exact username. Please enter the full username and try again.');
      } else {
        setSearchError('Search failed. Please try again.');
      }
    } catch {
      setSearchError('Network error. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleInvite = async () => {
    if (!foundUser) return;
    setInviting(true);
    try {
      const res = await fetch(`${baseUrl}/production/admin/invitations/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invited_user_id: foundUser.user_id,
          invited_email: null,
          role_offered: roleOffered || null,
          project_id: null,
        }),
      });
      if (res.ok) {
        setSuccessMsg(`✓ Invitation sent to @${foundUser.username} successfully!`);
        setFoundUser(null);
        setUsername('');
        setRoleOffered('');
        setTimeout(() => setSuccessMsg(''), 5000);
      } else {
        setSearchError('Failed to send invitation. Please try again.');
      }
    } catch {
      setSearchError('Network error. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  return (
    <>
      <div className="fm-card">
        <CardHead
          icon="userPlus"
          tone="amber"
          title="Enter username"
          subtitle="Type the exact username of the film enthusiast you want to invite."
        />

        <div className="fm-search-row">
          <div className="fm-search">
            <Ico name="search" size={17} className="fm-search-icon" />
            <span className="fm-at">@</span>
            <input
              type="text"
              value={username}
              onChange={e => { setUsername(e.target.value); setSearchError(''); setFoundUser(null); }}
              onKeyDown={handleKeyDown}
              placeholder="username (e.g. john_doe)"
              className="fm-input fm-search-input"
              aria-label="Exact username"
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching || !username.trim()}
            className="fm-btn fm-btn-primary fm-btn-lg"
          >
            {searching ? (<><span className="fm-spinner fm-spinner--sm" /> Searching...</>) : 'Search Enthusiast'}
          </button>
        </div>
        <p className="fm-tip">
          <Ico name="info" size={15} /> Press <kbd>Enter</kbd> to search quickly
        </p>
      </div>

      {/* Error */}
      {searchError && <Alert type="error">{searchError}</Alert>}

      {/* Success */}
      {successMsg && <Alert type="success">{successMsg}</Alert>}

      {/* Found user card */}
      {foundUser && (
        <div className="fm-card">
          <p className="fm-eyebrow">User found</p>
          <div className="fm-user">
            <div className="fm-avatar">
              {(foundUser.username || foundUser.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="fm-user-text">
              <p className="fm-user-name">
                {foundUser.full_name || foundUser.name || foundUser.username}
              </p>
              <p className="fm-user-handle">@{foundUser.username}</p>
              {foundUser.email && <p className="fm-user-email">{foundUser.email}</p>}
            </div>
            <Badge label={foundUser.user_type || 'Enthusiast'} color="blue" />
          </div>

          {/* Role selector */}
          <div className="fm-field">
            <label htmlFor="fm-role">Role offered <span className="fm-muted fm-small">(optional)</span></label>
            <select
              id="fm-role"
              value={roleOffered}
              onChange={e => setRoleOffered(e.target.value)}
              className="fm-input fm-select"
            >
              <option value="">— Select a role —</option>
              {ROLES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Invite button */}
          <div className="fm-actions">
            <button type="button" onClick={() => { setFoundUser(null); setUsername(''); }} className="fm-btn fm-btn-ghost">
              Cancel
            </button>
            <button type="button" onClick={handleInvite} disabled={inviting} className="fm-btn fm-btn-primary">
              <Ico name="send" size={16} /> {inviting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// Pending Connections Section
// ─────────────────────────────────────────────

const DAY_MS = 86400000;
const PENDING_FILTERS = [
  { id: 'all', label: 'All', maxDays: Infinity },
  { id: 'new', label: 'New', maxDays: 1 },
  { id: 'week', label: 'This Week', maxDays: 7 },
  { id: 'month', label: 'This Month', maxDays: 30 },
];

const PendingConnectionsSection = ({ token }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [toast, setToast] = useState('');
  // UI-only filters
  const [range, setRange] = useState('all');
  const [query, setQuery] = useState('');

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/production/admin/connections/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConnections(data);
      }
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const approveConnection = async id => {
    setProcessing(id);
    try {
      const res = await fetch(`${baseUrl}/production/admin/connections/${id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setConnections(prev => prev.filter(c => c.connection_id !== id));
        showToast('✓ Connection approved');
      }
    } catch {}
    finally { setProcessing(null); }
  };

  const showToast = msg => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const ageInDays = conn => {
    if (!conn.join_date) return null;
    const t = new Date(conn.join_date).getTime();
    return isNaN(t) ? null : (Date.now() - t) / DAY_MS;
  };

  const countFor = f => f.maxDays === Infinity
    ? connections.length
    : connections.filter(c => { const a = ageInDays(c); return a !== null && a <= f.maxDays; }).length;

  const activeFilter = PENDING_FILTERS.find(f => f.id === range) || PENDING_FILTERS[0];
  const q = query.trim().toLowerCase();
  const visible = connections.filter(c => {
    if (activeFilter.maxDays !== Infinity) {
      const a = ageInDays(c);
      if (a === null || a > activeFilter.maxDays) return false;
    }
    if (!q) return true;
    return [`user #${c.user_id}`, String(c.user_id), c.role_in_production]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  return (
    <div className="fm-card">
      <div className="fm-toolbar">
        <div className="fm-chips" role="group" aria-label="Filter by date">
          {PENDING_FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              className={`fm-chip ${range === f.id ? 'is-active' : ''}`}
              onClick={() => setRange(f.id)}
              aria-pressed={range === f.id}
            >
              {f.label}{f.id !== 'all' ? ` (${countFor(f)})` : ''}
            </button>
          ))}
        </div>
        <div className="fm-search fm-search--sm">
          <Ico name="search" size={16} className="fm-search-icon" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by user ID or role..."
            className="fm-input fm-search-input fm-search-input--plain"
            aria-label="Search pending requests"
          />
        </div>
      </div>

      {toast && <Alert type="success">{toast}</Alert>}

      {loading ? (
        <Spinner />
      ) : connections.length === 0 ? (
        <EmptyState
          image={pendingEmptyImg}
          title="No pending connection requests right now!"
          message="When enthusiasts request to join your Production House, they'll appear here for your review."
        />
      ) : visible.length === 0 ? (
        <EmptyState icon="search" message="No requests match this filter." />
      ) : (
        <div className="fm-list">
          {visible.map(conn => (
            <div key={conn.connection_id} className="fm-row">
              <div className="fm-avatar fm-avatar--sm fm-avatar--sky">
                {conn.user_id?.toString().charAt(0) || '?'}
              </div>
              <div className="fm-row-text">
                <p className="fm-row-title">User #{conn.user_id}</p>
                {conn.role_in_production && (
                  <p className="fm-row-meta">Role: {conn.role_in_production}</p>
                )}
                <p className="fm-row-meta">
                  Requested: {conn.join_date ? new Date(conn.join_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <Badge label="Pending" color="yellow" />
              <div className="fm-row-actions">
                <button
                  type="button"
                  onClick={() => approveConnection(conn.connection_id)}
                  disabled={processing === conn.connection_id}
                  className="fm-btn fm-btn-primary fm-btn-sm"
                >
                  {processing === conn.connection_id ? '...' : 'Approve'}
                </button>
                <button type="button" className="fm-btn fm-btn-danger-soft fm-btn-sm">
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Leave Requests Section (Admin View)
// ─────────────────────────────────────────────
// ⚠️ Requires backend: GET /production/admin/leaves/pending
//                       PUT /production/admin/leaves/{id}/review

const LeaveRequestsSection = ({ token }) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [toast, setToast] = useState({ msg: '', type: '' });

  const LEAVE_COLORS = {
    sick: 'red',
    vacation: 'blue',
    personal: 'teal',
    emergency: 'yellow',
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: '' }), 3500);
  };

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      // ⚠️ Backend endpoint needed: GET /production/admin/leaves/pending
      const res = await fetch(`${baseUrl}/production/admin/leaves/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeaves(data);
      } else {
        // endpoint may not exist yet — fail gracefully
        setLeaves([]);
      }
    } catch {
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const reviewLeave = async (id, action, notes = '') => {
    setProcessing(id);
    try {
      // ⚠️ Backend endpoint needed: PUT /production/admin/leaves/{id}/review
      const res = await fetch(`${baseUrl}/production/admin/leaves/${id}/review`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: action, review_notes: notes }),
      });
      if (res.ok) {
        setLeaves(prev => prev.filter(l => l.leave_id !== id));
        showToast(`Leave ${action === 'approved' ? 'approved' : 'rejected'} successfully.`, action === 'approved' ? 'success' : 'error');
      }
    } catch {}
    finally { setProcessing(null); }
  };

  return (
    <div className="fm-card">
      {toast.msg && <Alert type={toast.type === 'success' ? 'success' : 'error'}>{toast.msg}</Alert>}

      {loading ? (
        <Spinner />
      ) : leaves.length === 0 ? (
        <EmptyState
          image={leaveEmptyImg}
          title="No pending leave requests"
          message="Your team is all present! New leave applications will appear here."
        />
      ) : (
        <div className="fm-list">
          {leaves.map(leave => (
            <div key={leave.leave_id} className="fm-row fm-row--top">
              <div className="fm-row-text">
                <div className="fm-row-headline">
                  <span className="fm-row-title">User #{leave.user_id}</span>
                  <Badge label={leave.leave_type} color={LEAVE_COLORS[leave.leave_type] || 'gray'} />
                </div>
                <p className="fm-row-meta fm-row-meta--icon">
                  <Ico name="calendar" size={13} />
                  {new Date(leave.start_date).toLocaleDateString()} – {new Date(leave.end_date).toLocaleDateString()}
                </p>
                {leave.reason && <p className="fm-quote fm-quote--sm">"{leave.reason}"</p>}
              </div>
              <div className="fm-row-actions fm-row-actions--stack">
                <button
                  type="button"
                  onClick={() => reviewLeave(leave.leave_id, 'approved')}
                  disabled={processing === leave.leave_id}
                  className="fm-btn fm-btn-primary fm-btn-sm"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => reviewLeave(leave.leave_id, 'rejected')}
                  disabled={processing === leave.leave_id}
                  className="fm-btn fm-btn-danger-soft fm-btn-sm"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Feedback Section (Admin View)
// ─────────────────────────────────────────────
// ⚠️ Requires backend: GET /production/admin/feedback

const FeedbackSection = ({ token }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const CATEGORY_COLORS = {
    work_environment: 'green',
    management: 'blue',
    facilities: 'teal',
    other: 'gray',
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // ⚠️ Backend endpoint needed: GET /production/admin/feedback
        const res = await fetch(`${baseUrl}/production/admin/feedback`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setFeedbacks(await res.json());
        else setFeedbacks([]);
      } catch {
        setFeedbacks([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const renderStars = rating => (
    <div className="fm-stars" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={i <= rating ? 'is-on' : ''}>★</span>
      ))}
    </div>
  );

  return (
    <div className="fm-card">
      {loading ? (
        <Spinner />
      ) : feedbacks.length === 0 ? (
        <EmptyState icon="message" title="No feedback yet" message="Feedback shared by your production house members will appear here." />
      ) : (
        <div className="fm-list">
          {feedbacks.map(fb => (
            <div key={fb.feedback_id} className="fm-row fm-row--block">
              <div className="fm-row-headline fm-row-headline--split">
                <div className="fm-row-headline">
                  {fb.is_anonymous ? (
                    <span className="fm-muted fm-italic">Anonymous Member</span>
                  ) : (
                    <span className="fm-row-title">User #{fb.user_id}</span>
                  )}
                  {fb.category && (
                    <Badge label={fb.category.replace('_', ' ')} color={CATEGORY_COLORS[fb.category] || 'gray'} />
                  )}
                </div>
                {fb.rating && renderStars(fb.rating)}
              </div>
              <p className="fm-quote fm-quote--sm">"{fb.feedback_text}"</p>
              <p className="fm-row-meta">
                {new Date(fb.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Complaints Section (Admin View)
// ─────────────────────────────────────────────
// ⚠️ Requires backend: GET /production/admin/complaints
//                       PUT /production/admin/complaints/{id}/update

const ComplaintsSection = ({ token }) => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const PRIORITY_COLORS = { low: 'green', medium: 'yellow', high: 'red', urgent: 'red' };
  const STATUS_COLORS = { open: 'red', in_progress: 'yellow', resolved: 'green', closed: 'gray' };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // ⚠️ Backend endpoint needed: GET /production/admin/complaints
        const res = await fetch(`${baseUrl}/production/admin/complaints`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setComplaints(await res.json());
        else setComplaints([]);
      } catch {
        setComplaints([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      // ⚠️ Backend endpoint needed: PUT /production/admin/complaints/{id}/update
      const res = await fetch(`${baseUrl}/production/admin/complaints/${id}/update`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setComplaints(prev =>
          prev.map(c => c.complaint_id === id ? { ...c, status: newStatus } : c)
        );
      }
    } catch {}
    finally { setUpdatingId(null); }
  };

  return (
    <div className="fm-card">
      {loading ? (
        <Spinner />
      ) : complaints.length === 0 ? (
        <EmptyState
          image={complaintsEmptyImg}
          title="No complaints filed"
          message="Keep it up! Issues raised by your production team will appear here."
        />
      ) : (
        <div className="fm-list">
          {complaints.map(c => (
            <div key={c.complaint_id} className="fm-row fm-row--block">
              <div className="fm-row-headline fm-row-headline--split">
                <div>
                  <p className="fm-row-title">{c.subject}</p>
                  <div className="fm-badges">
                    <Badge label={c.complaint_type} color="blue" />
                    <Badge label={c.priority} color={PRIORITY_COLORS[c.priority] || 'gray'} />
                    <Badge label={c.status.replace('_', ' ')} color={STATUS_COLORS[c.status] || 'gray'} />
                  </div>
                </div>
                <select
                  value={c.status}
                  onChange={e => updateStatus(c.complaint_id, e.target.value)}
                  disabled={updatingId === c.complaint_id}
                  className="fm-input fm-select fm-select--sm"
                  aria-label="Complaint status"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <p className="fm-quote fm-quote--sm fm-quote--plain">{c.description}</p>
              <p className="fm-row-meta">
                Filed by User #{c.user_id} · {new Date(c.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Project Allocation Section
// ─────────────────────────────────────────────
// NOTE (kept as-is for later review): data loading + "Assign Members" / "Tools (Soon)"
// behaviour below is unchanged from the previous version — only the styling was updated.

const ProjectAllocationSection = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const uidRes = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!uidRes.ok) return;
        const { user_id } = await uidRes.json();
        const projRes = await fetch(`${baseUrl}/getProjectsByUserId`, {
          headers: { Authorization: `Bearer ${token}`, user_id },
        });
        if (projRes.ok) {
          const data = await projRes.json();
          setProjects(data.projects || []);
        }
      } catch {}
      finally { setLoading(false); }
    })();
  }, [token]);

  const STATUS_COLOR = {
    'Production': 'green',
    'Pre-Production': 'yellow',
    'Post-Production': 'blue',
    'Development': 'teal',
    'Completed': 'gray',
  };

  return (
    <div className="fm-card">
      {loading ? (
        <Spinner />
      ) : projects.length === 0 ? (
        <EmptyState
          image={projectsEmptyImg}
          title="No projects found"
          message="Create a project from the dashboard first, then assign your team members to it here."
        />
      ) : (
        <div className="fm-project-grid">
          {projects.map((proj, i) => (
            <div key={i} className="fm-project">
              <div className="fm-row-headline fm-row-headline--split">
                <div>
                  <p className="fm-row-title">{proj.title}</p>
                  <p className="fm-row-meta">
                    {proj.created_at ? new Date(proj.created_at).toLocaleDateString() : ''}
                  </p>
                </div>
                <Badge
                  label={proj.project_status || 'Pre-Production'}
                  color={STATUS_COLOR[proj.project_status] || 'yellow'}
                />
              </div>

              {/* Progress */}
              {proj.progress !== undefined && (
                <div className="fm-progress-wrap">
                  <div className="fm-progress-labels">
                    <span>Progress</span>
                    <span>{proj.progress || 0}%</span>
                  </div>
                  <div className="fm-progress">
                    <div className="fm-progress-bar" style={{ width: `${proj.progress || 0}%` }} />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="fm-project-actions">
                <button type="button" className="fm-btn fm-btn-outline fm-btn-sm">
                  <Ico name="users" size={15} /> Assign Members
                </button>
                <button type="button" className="fm-btn fm-btn-ghost fm-btn-sm" disabled title="Coming Soon">
                  <Ico name="tool" size={15} /> Tools <span className="fm-soon">Soon</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Overview Section
// ─────────────────────────────────────────────

const OverviewSection = ({ token, onNavigate, pendingCount }) => {
  const [stats, setStats] = useState({
    pending: 0,
    active: 0,
    invitations: 0,
    leaves: 0,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/production/admin/connections/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setStats(prev => ({ ...prev, pending: data.length }));
        }
      } catch {}
    })();
  }, [token]);

  const QUICK_ACTIONS = [
    { icon: 'key', tone: 'amber', title: 'Generate Invite Code', desc: 'Share a time-limited code to bring in new members.', section: 'invite-code' },
    { icon: 'userPlus', tone: 'sky', title: 'Invite by Username', desc: 'Search and invite a specific film enthusiast directly.', section: 'invite-username' },
    { icon: 'hourglass', tone: 'teal', title: 'Review Connections', desc: 'Approve or reject pending join requests.', section: 'pending', badge: pendingCount },
    { icon: 'calendar', tone: 'green', title: 'Leave Requests', desc: 'Review and action pending leave applications.', section: 'leaves' },
    { icon: 'message', tone: 'sky', title: 'View Feedback', desc: 'Read what your team has shared about the experience.', section: 'feedback' },
    { icon: 'alert', tone: 'rose', title: 'Complaints', desc: 'Track and resolve issues raised by your team.', section: 'complaints' },
    { icon: 'film', tone: 'amber', title: 'Project Allocation', desc: 'Assign enthusiasts to your active productions.', section: 'projects' },
  ];

  return (
    <>
      <div className="fm-card">
        <CardHead
          title="Film Management Overview"
          subtitle="Monitor your production house team, invitations, and activity at a glance."
        />
        <div className="fm-stats">
          <StatCard icon="hourglass" tone="amber" label="Pending Connections" desc="Requests waiting for your review" value={stats.pending} />
          <StatCard icon="users" tone="sky" label="Active Members" desc="Members in your production house" value={stats.active} />
          <StatCard icon="mail" tone="teal" label="Sent Invitations" desc="Invitations sent to collaborators" value={stats.invitations} />
          <StatCard icon="fileText" tone="green" label="Leave Requests" desc="Pending leave applications" value={stats.leaves} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="fm-card">
        <CardHead title="Quick actions" subtitle="Jump straight into any part of Film Management." />
        <div className="fm-quick-grid">
          {QUICK_ACTIONS.map(item => (
            <button
              key={item.section}
              type="button"
              className="fm-quick"
              onClick={() => onNavigate(item.section)}
            >
              <span className={`fm-tile fm-tile--sm fm-tone-${item.tone}`}><Ico name={item.icon} size={20} /></span>
              <span className="fm-quick-text">
                <span className="fm-quick-title">
                  {item.title}
                  {item.badge > 0 && <span className="fm-count">{item.badge}</span>}
                </span>
                <span className="fm-quick-desc">{item.desc}</span>
              </span>
              <Ico name="chevronRight" size={16} className="fm-quick-chevron" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// Banner copy per section
// ─────────────────────────────────────────────

const BANNERS = {
  'overview':        { title: 'Film Management', subtitle: 'Monitor your production house team, invitations and activity at a glance.' },
  'invite-username': { title: 'Invite Enthusiast by Username', subtitle: 'Search and invite a specific film enthusiast to join your production team. Enter the exact username for the best result.' },
  'invite-code':     { title: 'Invite via Production House Code', subtitle: 'Generate a time-limited security code for your team to join this Production House.' },
  'pending':         { title: 'Pending Connection Requests', subtitle: 'Review and approve enthusiasts waiting to join your Production House.' },
  'leaves':          { title: 'Leave Requests', subtitle: 'Review and action pending leave applications from your production team.' },
  'feedback':        { title: 'Team Feedback', subtitle: 'Feedback submitted by your production house members.' },
  'complaints':      { title: 'Complaints & Issues', subtitle: 'Manage complaints raised by your production team members.' },
  'projects':        { title: 'Project Allocation', subtitle: 'Assign team members to projects. Tool allocation within projects is coming soon.' },
};

// ─────────────────────────────────────────────
// Main FilmManagement Component
// ─────────────────────────────────────────────

const FilmManagement = ({ section, onSectionChange } = {}) => {
  const token = localStorage.getItem('access_token');
  const [internalSection, setInternalSection] = useState('overview');
  const [pendingCount, setPendingCount] = useState(0);

  // Controlled by the dashboard sidebar flyout when props are passed; otherwise self-managed
  const activeSection = section || internalSection;
  const setActiveSection = onSectionChange || setInternalSection;

  // Fetch pending count (shown on the "Review Connections" quick action)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/production/admin/connections/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setPendingCount(data.length);
        }
      } catch {}
    })();
  }, [token]);

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':        return <OverviewSection token={token} onNavigate={setActiveSection} pendingCount={pendingCount} />;
      case 'invite-username': return <InviteByUsernameSection token={token} />;
      case 'invite-code':     return <InviteByCodeSection token={token} />;
      case 'pending':         return <PendingConnectionsSection token={token} />;
      case 'leaves':          return <LeaveRequestsSection token={token} />;
      case 'feedback':        return <FeedbackSection token={token} />;
      case 'complaints':      return <ComplaintsSection token={token} />;
      case 'projects':        return <ProjectAllocationSection token={token} />;
      default:                return <ComingSoon feature="This Section" />;
    }
  };

  const meta = FILM_SECTIONS.find(s => s.id === activeSection) || FILM_SECTIONS[0];
  const banner = BANNERS[activeSection] || BANNERS.overview;

  return (
    <div className="fm-root">
      <style>{FM_STYLES}</style>
      <Banner
        sectionLabel={meta.label}
        title={banner.title}
        subtitle={banner.subtitle}
        onRoot={() => setActiveSection('overview')}
        showIcon={activeSection === 'overview'}
      />
      <div className="fm-content" key={activeSection}>
        {renderSection()}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Styles (scoped fm- classes, kept in this file)
// ─────────────────────────────────────────────

const FM_STYLES = `
.fm-root {
  --fm-teal: #0fb3ad; --fm-teal-hover: #0c9a95; --fm-teal-light: #5eead4;
  --fm-teal-soft: rgba(15,179,173,0.12); --fm-teal-border: rgba(15,179,173,0.45);
  --fm-surface: #1b2433; --fm-surface-2: #222d3f; --fm-border: rgba(255,255,255,0.08);
  --fm-text: #f3f5f7; --fm-muted: #94a3b8;
  color: var(--fm-text);
}
.fm-root button { font-family: inherit; }
.fm-root button:focus-visible, .fm-root input:focus-visible, .fm-root select:focus-visible, .fm-root textarea:focus-visible {
  outline: 2px solid var(--fm-teal-light); outline-offset: 2px;
}
.fm-muted { color: var(--fm-muted); }
.fm-small { font-size: 12px; font-weight: 400; }
.fm-italic { font-style: italic; font-size: 14px; }

/* Banner */
.fm-banner { position: relative; min-height: 190px; border-radius: 16px; overflow: hidden; border: 1px solid var(--fm-border); }
.fm-banner-bg { position: absolute; inset: 0; background-size: cover; background-position: center 45%; filter: brightness(1.08) saturate(1.15); }
.fm-banner-overlay { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(8,15,25,0.94) 0%, rgba(8,15,25,0.7) 42%, rgba(8,15,25,0.1) 78%, rgba(8,15,25,0.2) 100%); }
.fm-banner-content { position: relative; z-index: 1; min-height: 190px; display: flex; flex-direction: column; padding: 16px 28px 26px; }
.fm-crumbs { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #cbd5e1; }
.fm-crumbs button { padding: 0; border: none; background: none; color: #cbd5e1; font-size: 13px; cursor: pointer; }
.fm-crumbs button:hover { color: var(--fm-teal-light); }
.fm-crumb-sep { opacity: .6; }
.fm-crumb-current { color: #fff; font-weight: 600; }
.fm-banner-main { display: flex; align-items: center; gap: 16px; margin-top: auto; padding-top: 22px; }
.fm-banner-icon { width: 52px; height: 52px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  border-radius: 12px; background: rgba(15,179,173,0.18); border: 1px solid var(--fm-teal-border); color: var(--fm-teal-light); }
.fm-banner-title { margin: 0; font-size: 22px; font-weight: 700; color: #fff; }
.fm-banner-sub { margin: 6px 0 0; max-width: 520px; font-size: 14px; line-height: 1.55; color: #cbd5e1; }

/* Layout */
.fm-content { display: flex; flex-direction: column; gap: 20px; margin-top: 20px; }
.fm-card { padding: 22px 24px; background: var(--fm-surface); border: 1px solid var(--fm-border); border-radius: 16px; }
.fm-card-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px; }
.fm-card-head-text { flex: 1; min-width: 0; }
.fm-card-title { margin: 0; font-size: 17px; font-weight: 600; color: var(--fm-text); }
.fm-card-sub { margin: 4px 0 0; font-size: 14px; color: var(--fm-muted); }
.fm-subsection { margin-top: 26px; }
.fm-indent { margin-left: 58px; }
.fm-eyebrow { margin: 0 0 8px; font-size: 12px; font-weight: 600; color: var(--fm-muted); }

/* Tiles & tones */
.fm-tile { width: 52px; height: 52px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 12px; }
.fm-tile--sm { width: 44px; height: 44px; border-radius: 10px; }
.fm-tone-teal  { background: rgba(15,179,173,0.16); color: #5eead4; }
.fm-tone-amber { background: rgba(245,158,11,0.16); color: #fcd34d; }
.fm-tone-sky   { background: rgba(14,165,233,0.16); color: #7dd3fc; }
.fm-tone-green { background: rgba(16,185,129,0.16); color: #6ee7b7; }
.fm-tone-rose  { background: rgba(244,63,94,0.16);  color: #fda4af; }

/* Stats */
.fm-stats { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 14px; }
.fm-stat { display: flex; align-items: flex-start; gap: 14px; padding: 16px; background: var(--fm-surface-2);
  border: 1px solid var(--fm-border); border-radius: 14px; }
.fm-stat-text { min-width: 0; }
.fm-stat-value { font-size: 24px; font-weight: 700; line-height: 1.1; }
.fm-stat-label { margin-top: 4px; font-size: 14px; font-weight: 600; }
.fm-stat-desc { margin-top: 3px; font-size: 12.5px; line-height: 1.4; color: var(--fm-muted); }

/* Quick actions */
.fm-quick-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.fm-quick { display: flex; align-items: center; gap: 14px; padding: 16px; text-align: left; cursor: pointer;
  background: var(--fm-surface-2); border: 1px solid var(--fm-border); border-radius: 14px; color: var(--fm-text);
  transition: border-color .15s ease; }
.fm-quick:hover { border-color: var(--fm-teal-border); }
.fm-quick-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.fm-quick-title { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; }
.fm-quick-desc { margin-top: 3px; font-size: 13px; line-height: 1.45; color: var(--fm-muted); }
.fm-quick-chevron { color: var(--fm-muted); }
.fm-count { min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: #ef4444; color: #fff;
  font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }

/* Inputs */
.fm-input { width: 100%; padding: 10px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px; color: var(--fm-text); font-size: 14px; outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
.fm-input::placeholder { color: #7c8a9e; }
.fm-input:focus { border-color: var(--fm-teal); box-shadow: 0 0 0 3px rgba(15,179,173,0.2); }
.fm-textarea { resize: vertical; line-height: 1.55; }
.fm-select { appearance: none; -webkit-appearance: none; padding-right: 36px; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center; }
.fm-select option { background: #1b2433; color: #f3f5f7; }
.fm-select--sm { width: auto; padding: 7px 34px 7px 10px; font-size: 13px; flex-shrink: 0; }
.fm-field { margin-top: 16px; }
.fm-field label { display: block; margin-bottom: 6px; font-size: 14px; font-weight: 600; }

.fm-search-row { display: flex; gap: 12px; }
.fm-search { position: relative; flex: 1; min-width: 0; }
.fm-search--sm { flex: 0 1 300px; }
.fm-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--fm-muted); pointer-events: none; }
.fm-at { position: absolute; left: 40px; top: 50%; transform: translateY(-50%); color: var(--fm-muted); font-size: 15px; pointer-events: none; }
.fm-search-input { height: 46px; padding-left: 58px; font-size: 15px; }
.fm-search-input--plain { height: 42px; padding-left: 40px; font-size: 14px; }
.fm-tip { display: flex; align-items: center; gap: 8px; margin: 12px 0 0; font-size: 13px; color: var(--fm-muted); }
.fm-tip kbd { padding: 2px 8px; border-radius: 6px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
  color: #e2e8f0; font-family: inherit; font-size: 12px; }

.fm-quote { margin: 0; padding: 14px 16px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; font-size: 14px; line-height: 1.6; color: #cbd5e1; }
.fm-quote--sm { margin-top: 10px; padding: 10px 12px; font-size: 13.5px; font-style: italic; }
.fm-quote--plain { font-style: normal; }

/* Pills / chips */
.fm-pills { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 10px; }
.fm-pill { height: 40px; padding: 0 12px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.03); color: #e2e8f0; font-size: 14px; font-weight: 500; cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease; }
.fm-pill:hover { border-color: var(--fm-teal-border); }
.fm-pill.is-active { background: var(--fm-teal); border-color: var(--fm-teal); color: #fff; }
.fm-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.fm-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.fm-chip { height: 34px; padding: 0 14px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08);
  background: rgba(0,0,0,0.25); color: #e2e8f0; font-size: 14px; font-weight: 500; cursor: pointer; }
.fm-chip:hover { border-color: var(--fm-teal-border); }
.fm-chip.is-active { background: var(--fm-teal); border-color: var(--fm-teal); color: #fff; }

/* Buttons */
.fm-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; }
.fm-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 18px; border-radius: 10px;
  border: 1px solid transparent; font-size: 14px; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease; }
.fm-btn:disabled { opacity: .55; cursor: not-allowed; }
.fm-btn-sm { padding: 7px 12px; font-size: 13px; }
.fm-btn-lg { height: 46px; padding: 0 26px; }
.fm-btn-primary { background: var(--fm-teal); color: #fff; }
.fm-btn-primary:hover:not(:disabled) { background: var(--fm-teal-hover); }
.fm-btn-ghost { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.14); color: var(--fm-text); }
.fm-btn-ghost:hover:not(:disabled) { border-color: var(--fm-teal); color: var(--fm-teal-light); }
.fm-btn-outline { background: var(--fm-teal-soft); border-color: var(--fm-teal-border); color: var(--fm-teal-light); }
.fm-btn-outline:hover:not(:disabled) { background: var(--fm-teal); color: #fff; }
.fm-btn-success { background: #16a34a; color: #fff; }
.fm-btn-danger-soft { background: rgba(248,113,113,0.12); border-color: rgba(248,113,113,0.35); color: #fca5a5; }
.fm-btn-danger-soft:hover:not(:disabled) { background: rgba(248,113,113,0.2); }
.fm-link { display: inline-flex; align-items: center; gap: 6px; padding: 0; border: none; background: none;
  color: var(--fm-teal-light); font-size: 13px; font-weight: 500; cursor: pointer; }
.fm-link:hover { color: #fff; }
.fm-link--danger { color: #fca5a5; }
.fm-soon { margin-left: 2px; padding: 1px 6px; border-radius: 6px; background: rgba(255,255,255,0.1); font-size: 11px; }

/* Alerts */
.fm-alert { padding: 12px 16px; border-radius: 12px; font-size: 14px; font-weight: 500; }
.fm-card .fm-alert { margin-top: 16px; }
.fm-alert--success { background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: #86efac; }
.fm-alert--error { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.3); color: #fca5a5; }

/* Badges */
.fm-badge { display: inline-block; padding: 2px 9px; border-radius: 999px; border: 1px solid transparent; font-size: 12px;
  font-weight: 500; text-transform: capitalize; white-space: nowrap; }
.fm-badge--green { background: rgba(34,197,94,0.14); border-color: rgba(34,197,94,0.35); color: #86efac; }
.fm-badge--yellow { background: rgba(245,158,11,0.14); border-color: rgba(245,158,11,0.35); color: #fcd34d; }
.fm-badge--red { background: rgba(248,113,113,0.14); border-color: rgba(248,113,113,0.35); color: #fca5a5; }
.fm-badge--blue { background: rgba(14,165,233,0.14); border-color: rgba(14,165,233,0.35); color: #7dd3fc; }
.fm-badge--teal, .fm-badge--purple { background: var(--fm-teal-soft); border-color: var(--fm-teal-border); color: var(--fm-teal-light); }
.fm-badge--gray { background: rgba(148,163,184,0.14); border-color: rgba(148,163,184,0.3); color: #cbd5e1; }
.fm-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }

/* Lists */
.fm-list { display: flex; flex-direction: column; gap: 12px; }
.fm-row { display: flex; align-items: center; gap: 14px; padding: 16px; background: var(--fm-surface-2);
  border: 1px solid var(--fm-border); border-radius: 12px; transition: border-color .15s ease; }
.fm-row:hover { border-color: rgba(255,255,255,0.16); }
.fm-row--top { align-items: flex-start; }
.fm-row--block { display: block; }
.fm-row-text { flex: 1; min-width: 0; }
.fm-row-headline { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.fm-row-headline--split { justify-content: space-between; align-items: flex-start; }
.fm-row-title { margin: 0; font-size: 14.5px; font-weight: 600; }
.fm-row-meta { margin: 4px 0 0; font-size: 12.5px; color: var(--fm-muted); }
.fm-row-meta--icon { display: flex; align-items: center; gap: 6px; margin-top: 8px; }
.fm-row-actions { display: flex; gap: 8px; flex-shrink: 0; }
.fm-row-actions--stack { flex-direction: column; }
.fm-stars { display: flex; gap: 2px; color: #475569; }
.fm-stars .is-on { color: #fcd34d; }

/* Avatars / user */
.fm-avatar { width: 48px; height: 48px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 50%;
  background: rgba(245,158,11,0.16); border: 1px solid rgba(245,158,11,0.35); color: #fcd34d; font-weight: 700; font-size: 18px; }
.fm-avatar--sm { width: 40px; height: 40px; font-size: 15px; }
.fm-avatar--sky { background: rgba(14,165,233,0.16); border-color: rgba(14,165,233,0.35); color: #7dd3fc; }
.fm-user { display: flex; align-items: center; gap: 14px; padding: 14px; background: var(--fm-surface-2);
  border: 1px solid var(--fm-border); border-radius: 12px; }
.fm-user-text { flex: 1; min-width: 0; }
.fm-user-name { margin: 0; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fm-user-handle { margin: 2px 0 0; font-size: 14px; color: var(--fm-muted); }
.fm-user-email { margin: 2px 0 0; font-size: 12.5px; color: #64748b; }

/* Invitation code panel */
.fm-code-panel { margin-top: 24px; padding: 20px; background: rgba(15,179,173,0.06); border: 1px solid var(--fm-teal-border); border-radius: 14px; }
.fm-code-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.fm-code-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.fm-code { padding: 8px 16px; border-radius: 10px; background: rgba(0,0,0,0.3); border: 1px solid var(--fm-teal-border);
  color: var(--fm-teal-light); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 26px; font-weight: 700; letter-spacing: .25em; }
.fm-countdown { text-align: right; }
.fm-countdown span { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 18px; font-weight: 700; color: #86efac; }
.fm-countdown span.is-expired { color: #fca5a5; }
.fm-note { display: flex; gap: 12px; margin-top: 18px; padding: 14px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; font-size: 14px; line-height: 1.55; color: #cbd5e1; }
.fm-note p { margin: 0; }
.fm-note strong { color: #fff; }
.fm-note-icon { color: var(--fm-teal-light); margin-top: 2px; }
.fm-note-warn { margin-top: 6px !important; font-size: 12.5px; color: #fcd34d; }
.fm-preview { margin-top: 14px; }
.fm-preview-text { margin: 0; font-size: 14px; font-style: italic; color: #cbd5e1; }
.fm-code-actions { display: flex; justify-content: space-between; margin-top: 16px; }

/* Projects */
.fm-project-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
.fm-project { padding: 18px; background: var(--fm-surface-2); border: 1px solid var(--fm-border); border-radius: 14px; }
.fm-progress-wrap { margin-top: 16px; }
.fm-progress-labels { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 12.5px; color: var(--fm-muted); }
.fm-progress { height: 6px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; }
.fm-progress-bar { height: 100%; border-radius: 999px; background: var(--fm-teal); }
.fm-project-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 16px; }

/* Empty / loading */
.fm-empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 28px 16px 20px; }
.fm-empty-img { width: 100%; max-width: 220px; height: auto; margin-bottom: 18px; border-radius: 14px; }
.fm-empty-icon { width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
  border-radius: 16px; background: var(--fm-teal-soft); color: var(--fm-teal); }
.fm-empty-title { margin: 0; font-size: 16px; font-weight: 600; }
.fm-empty-msg { margin: 6px 0 0; max-width: 380px; font-size: 14px; line-height: 1.55; color: var(--fm-muted); }
.fm-spinner-wrap { display: flex; justify-content: center; padding: 36px; }
.fm-spinner { width: 30px; height: 30px; border-radius: 50%; border: 3px solid rgba(255,255,255,0.1); border-top-color: var(--fm-teal);
  animation: fm-spin .8s linear infinite; }
.fm-spinner--sm { width: 15px; height: 15px; border-width: 2px; border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes fm-spin { to { transform: rotate(360deg); } }

/* Responsive */
@media (max-width: 1100px) { .fm-stats { grid-template-columns: repeat(2, minmax(0,1fr)); } }
@media (max-width: 768px) {
  .fm-banner-content { padding: 14px 18px 20px; }
  .fm-banner-title { font-size: 19px; }
  .fm-card { padding: 18px 16px; }
  .fm-stats { grid-template-columns: 1fr; }
  .fm-indent { margin-left: 0; }
  .fm-search-row { flex-direction: column; }
  .fm-search--sm { flex: 1 1 100%; }
  .fm-row { flex-wrap: wrap; }
  .fm-actions { flex-direction: column-reverse; }
}
@media (prefers-reduced-motion: reduce) {
  .fm-spinner { animation: none; }
  .fm-quick, .fm-btn, .fm-pill, .fm-row { transition: none; }
}
`;

export default FilmManagement;